import { baseApi } from "configs/store-query";
import {
  AuthenticationSignOutApiRequest,
  AuthenticationSignOutApiResponse,
} from "apis/authentication/types/authentication-sign-out-api";
import {
  AuthenticationRefreshApiRequest,
  AuthenticationRefreshApiResponse,
} from "apis/authentication/types/authentication-refresh-api";
import {
  AuthenticationInvitationAcceptAsFreelancerApiRequest,
  AuthenticationInvitationAcceptAsFreelancerApiResponse,
} from "apis/authentication/types/authentication-invitation-accept-as-freelancer-api";
import {
  ListSwitchableAccountAuthenticationApiRequest,
  ListSwitchableAccountAuthenticationApiResponse,
  SwitchAccountAuthenticationApiRequest,
  SwitchAccountAuthenticationApiResponse,
} from "apis/authentication/types/authentication-switch-account-api";
import { INVITATION, JOB } from "constants/tags";

const BASE_PATH = "/authentication";

export const authenticationController = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Signs the current session out server-side, revoking its access and
     * refresh tokens so they can no longer be reused.
     */
    signOutAuthentication: build.mutation<
      AuthenticationSignOutApiResponse,
      AuthenticationSignOutApiRequest
    >({
      query: (config) => ({
        url: `${BASE_PATH}/sign-out`,
        method: "POST",
        ...config,
      }),
    }),
    /**
     * Exchanges a refresh token for a fresh access token, keeping the session
     * alive. The fulfilled response updates `authUser` via a store-slice matcher.
     */
    refreshAuthentication: build.mutation<
      AuthenticationRefreshApiResponse,
      AuthenticationRefreshApiRequest
    >({
      query: (config) => ({
        url: `${BASE_PATH}/refresh`,
        method: "POST",
        ...config,
      }),
    }),
    /** Lists the accounts the signed-in user can switch their session to. */
    listSwitchableAccountAuthentication: build.query<
      ListSwitchableAccountAuthenticationApiResponse,
      ListSwitchableAccountAuthenticationApiRequest
    >({
      query: (config) => ({
        url: `${BASE_PATH}/accounts`,
        method: "GET",
        ...config,
      }),
      // Accepting a team/account invitation adds an account to switch to.
      providesTags: [INVITATION],
    }),
    /**
     * Switches the session to another account the user owns or is a member
     * of. The fulfilled response replaces `authUser` via a store-slice matcher.
     */
    switchAccountAuthentication: build.mutation<
      SwitchAccountAuthenticationApiResponse,
      SwitchAccountAuthenticationApiRequest
    >({
      query: (config) => ({
        url: `${BASE_PATH}/switch-account`,
        method: "POST",
        ...config,
      }),
    }),
    /**
     * Accepts a job invitation for a user whose login has no freelancer
     * account yet: the API creates one (made primary), accepts the invitation
     * with it and returns a new session for it. The fulfilled response
     * replaces `authUser`'s tokens via a store-slice matcher.
     */
    acceptJobInvitationAsFreelancerAuthentication: build.mutation<
      AuthenticationInvitationAcceptAsFreelancerApiResponse,
      AuthenticationInvitationAcceptAsFreelancerApiRequest
    >({
      query: ({ path, ...config }) => ({
        url: `${BASE_PATH}/invitations/${path.id}/accept-as-freelancer`,
        method: "POST",
        ...config,
      }),
      invalidatesTags: [JOB, INVITATION],
    }),
  }),
});
